import { useState } from "react";
import { motion } from "framer-motion";
import { Music, Mail, User, Link as LinkIcon, MessageSquare, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const DemoPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    artist_name: "",
    demo_link: "",
    message: "",
    consent_rgpd: false,
    honeypot: "" // Anti-spam hidden field
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_rgpd) {
      toast.error("Veuillez accepter la politique de confidentialité");
      return;
    }

    if (!formData.demo_link.startsWith("http")) {
      toast.error("Le lien de démo doit être une URL valide (commençant par http)");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/demo`, formData);
      setSubmitted(true);
      toast.success("Démo envoyée avec succès !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-12 px-6 md:px-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-4">Merci pour votre soumission !</h1>
          <p className="text-[#A8A29E] mb-8">
            Nous avons bien reçu votre démo et nous l'écouterons attentivement. 
            Si votre style correspond à notre ligne artistique, nous vous recontacterons.
          </p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            Retour à l'accueil
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 md:px-12" data-testid="demo-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
            ARTISTES
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Envoyer une démo
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-xl mx-auto">
            Vous êtes producteur ou artiste ? Partagez votre musique avec nous. 
            Nous écoutons toutes les démos reçues.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-8"
          data-testid="demo-form"
        >
          {/* Honeypot - hidden from users */}
          <input
            type="text"
            name="honeypot"
            value={formData.honeypot}
            onChange={handleChange}
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
          />

          {/* Email */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Email <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="votre@email.com"
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                data-testid="demo-email"
              />
            </div>
          </div>

          {/* Artist Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Nom d'artiste <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <input
                type="text"
                name="artist_name"
                value={formData.artist_name}
                onChange={handleChange}
                required
                placeholder="Votre nom d'artiste ou projet"
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                data-testid="demo-artist"
              />
            </div>
          </div>

          {/* Demo Link */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Lien de la démo <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <LinkIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <input
                type="url"
                name="demo_link"
                value={formData.demo_link}
                onChange={handleChange}
                required
                placeholder="https://soundcloud.com/... ou lien WeTransfer/Drive"
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                data-testid="demo-link"
              />
            </div>
            <p className="text-xs text-[#A8A29E] mt-2">
              SoundCloud, Dropbox, Google Drive, WeTransfer ou tout autre lien d'écoute
            </p>
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              Message (optionnel)
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-[#A8A29E] pointer-events-none" />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Présentez-vous brièvement, parlez de votre style..."
                className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 pl-14 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none resize-none"
                data-testid="demo-message"
              />
            </div>
          </div>

          {/* RGPD Consent */}
          <div className="mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="consent_rgpd"
                checked={formData.consent_rgpd}
                onChange={handleChange}
                className="mt-1 w-5 h-5 rounded border-[rgba(139,69,19,0.3)] bg-[#0A0908] text-[#FFB347] focus:ring-[#FFB347]"
                data-testid="demo-consent"
              />
              <span className="text-sm text-[#A8A29E]">
                J'accepte que mes données soient traitées par Sapito-Records pour l'examen de ma démo, 
                conformément à la{" "}
                <Link to="/mentions-legales" className="text-[#FFB347] hover:underline">
                  politique de confidentialité
                </Link>
                . <span className="text-red-400">*</span>
              </span>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="demo-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Music className="w-5 h-5" />
                Envoyer ma démo
              </>
            )}
          </button>
        </motion.form>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 p-6 bg-[#141210]/50 rounded-xl border border-[rgba(139,69,19,0.1)]"
        >
          <h3 className="font-display font-semibold mb-3">Ce que nous recherchons</h3>
          <ul className="space-y-2 text-sm text-[#A8A29E]">
            <li className="flex items-start gap-2">
              <span className="text-[#FFB347]">•</span>
              House, Deep House, Tech House, Techno, Melodic Techno
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FFB347]">•</span>
              Productions originales uniquement
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#FFB347]">•</span>
              Qualité audio professionnelle (WAV ou MP3 320kbps minimum)
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default DemoPage;
